/**
 * 조직(부서) 관리
 * ---------------------------------------------------------------------------
 * 서버가 parent_id 와 dept_path 로 트리를 유지한다.
 * 부서명이나 상위 부서를 바꾸면 하위 조직의 경로도 함께 갱신되므로,
 * 화면에서는 저장 후 트리 전체를 다시 불러온다.
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, DownloadOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import {
  errorText,
  flattenDepartments,
  qk,
  useDepartments,
  type DepartmentNode,
} from '../../lib/queries';
import { hasPermission, useAuth } from '../../lib/auth-store';
import { downloadCsv } from '../../lib/csv';

/**
 * 트리를 한 줄씩 펼친다.
 *
 * 화면은 접었다 폈다 하지만 파일을 받는 쪽은 **전부 펼쳐진 목록**을
 * 기대한다. 깊이와 경로를 함께 담으므로 받은 뒤에도 계층을 복원할 수 있다.
 */
function flattenTree(nodes: DepartmentNode[]): DepartmentNode[] {
  const out: DepartmentNode[] = [];
  const walk = (list: DepartmentNode[]) => {
    for (const node of list) {
      out.push(node);
      if (node.children?.length) walk(node.children);
    }
  };
  walk(nodes);
  return out;
}

const { Text } = Typography;

interface FormValue {
  departmentId?: string;
  parentId?: string | null;
  deptCode: string;
  deptName: string;
  sortOrder?: number;
  isActive?: boolean;
}

export default function DepartmentPage() {
  const { profile } = useAuth();
  const { message } = AntdApp.useApp();
  const client = useQueryClient();
  const [form] = Form.useForm<FormValue>();

  const canManage = hasPermission(profile, 'platform.dept:manage');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DepartmentNode | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tree = useDepartments(includeInactive);
  const refresh = () => client.invalidateQueries({ queryKey: qk.departments });

  /** 상위 부서 선택지. 수정 중인 부서와 그 하위는 제외해야 순환이 생기지 않는다. */
  const parentOptions = flattenDepartments(tree.data ?? []).filter((option) => {
    if (!editing) return true;
    if (option.value === editing.departmentId) return false;
    const node = findNode(tree.data ?? [], option.value);
    return !node?.deptPath.startsWith(`${editing.deptPath}/`);
  });

  /**
   * 모달이 열린 뒤에 값을 채운다.
   *
   * destroyOnHidden 이라 닫혀 있는 동안에는 <Form> 이 DOM 에 없다.
   * 그때 form 인스턴스를 건드리면 antd 가
   * "useForm is not connected to any Form element" 경고를 낸다.
   */
  const [pending, setPending] = useState<FormValue | null>(null);

  const applyOnOpen = (opened: boolean) => {
    if (!opened || !pending) return;
    form.resetFields();
    form.setFieldsValue(pending);
  };

  const openCreate = (parent?: DepartmentNode) => {
    setEditing(null);
    setError(null);
    setPending({
      parentId: parent?.departmentId ?? null,
      deptCode: '',
      deptName: '',
      isActive: true,
      sortOrder: 0,
    });
    setOpen(true);
  };

  const openEdit = (node: DepartmentNode) => {
    setEditing(node);
    setError(null);
    setPending({
      departmentId: node.departmentId,
      parentId: node.parentId,
      deptCode: node.deptCode,
      deptName: node.deptName,
      sortOrder: node.sortOrder,
      isActive: node.isActive,
    });
    setOpen(true);
  };

  const submit = async () => {
    let values: FormValue;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        parentId: values.parentId ?? null,
        deptCode: values.deptCode,
        deptName: values.deptName,
        sortOrder: values.sortOrder ?? 0,
        isActive: values.isActive ?? true,
      };
      if (editing) {
        await apiClient.patch(`/tenant/departments/${editing.departmentId}`, payload);
        message.success('부서 정보를 저장했습니다.');
      } else {
        await apiClient.post('/tenant/departments', payload);
        message.success('부서를 추가했습니다.');
      }
      setOpen(false);
      await refresh();
    } catch (err) {
      setError(errorText(err as NormalizedError));
    } finally {
      setSaving(false);
    }
  };

  const remove = (node: DepartmentNode) => {
    Modal.confirm({
      title: `'${node.deptName}' 부서를 삭제하시겠습니까?`,
      content: '하위 부서나 소속 구성원이 있으면 삭제할 수 없습니다.',
      okText: '삭제',
      okButtonProps: { danger: true },
      cancelText: '취소',
      async onOk() {
        try {
          await apiClient.delete(`/tenant/departments/${node.departmentId}`);
          message.success('부서를 삭제했습니다.');
          await refresh();
        } catch (err) {
          message.error(errorText(err as NormalizedError));
          throw err;
        }
      },
    });
  };

  const columns: ColumnsType<DepartmentNode> = [
    {
      title: '부서명',
      dataIndex: 'deptName',
      key: 'deptName',
      render: (value: string, row) => (
        <Space>
          <strong>{value}</strong>
          {!row.isActive && <Tag>미사용</Tag>}
        </Space>
      ),
    },
    { title: '코드', dataIndex: 'deptCode', key: 'deptCode', width: 140 },
    {
      title: '인원',
      dataIndex: 'memberCount',
      key: 'memberCount',
      width: 90,
      render: (n: number) => (n > 0 ? `${n}명` : <Text type="secondary">-</Text>),
    },
    { title: '정렬', dataIndex: 'sortOrder', key: 'sortOrder', width: 80 },
    {
      title: '관리',
      key: 'actions',
      width: 150,
      render: (_, row) => (
        <Space size={4}>
          <Button
            size="small"
            type="text"
            icon={<PlusOutlined />}
            disabled={!canManage}
            onClick={() => openCreate(row)}
            title="하위 부서 추가"
          />
          <Button
            size="small"
            type="text"
            icon={<EditOutlined />}
            disabled={!canManage}
            onClick={() => openEdit(row)}
          />
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            disabled={!canManage}
            onClick={() => remove(row)}
          />
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small">
        <Space wrap>
          {/*
            조직도를 파일로 받는다. 화면은 트리라 접힌 가지가 보이지 않지만,
            파일에는 **전부** 담는다 — 받는 쪽은 펼쳐 놓은 목록을 기대한다.
            계층은 「경로」 칸(/회사/본부/팀)으로 알 수 있다.
          */}
          <Button
            icon={<DownloadOutlined />}
            disabled={(tree.data ?? []).length === 0}
            onClick={() => downloadCsv('조직도', flattenTree(tree.data ?? []), [
              { header: '단계', value: (r) => r.depth },
              { header: '부서코드', value: (r) => r.deptCode },
              { header: '부서명', value: (r) => r.deptName },
              { header: '경로', value: (r) => r.deptPath },
              { header: '구성원 수', value: (r) => r.memberCount },
              { header: '정렬', value: (r) => r.sortOrder },
              { header: '상태', value: (r) => (r.isActive ? '사용' : '사용 안 함') },
            ])}
          >
            엑셀 내려받기
          </Button>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!canManage}
            onClick={() => openCreate()}
          >
            최상위 부서 추가
          </Button>
          <Space size={6}>
            <Switch checked={includeInactive} onChange={setIncludeInactive} size="small" />
            <Text type="secondary">미사용 부서 포함</Text>
          </Space>
        </Space>
      </Card>

      {tree.isError && <Alert type="error" showIcon message={errorText(tree.error)} />}

      <Table<DepartmentNode>
        rowKey="departmentId"
        columns={columns}
        dataSource={tree.data ?? []}
        loading={tree.isLoading}
        pagination={false}
        size="middle"
        expandable={{ defaultExpandAllRows: true, childrenColumnName: 'children' }}
        locale={{ emptyText: '등록된 부서가 없습니다.' }}
      />

      <Modal
        open={open}
        title={editing ? '부서 수정' : '부서 추가'}
        okText="저장"
        cancelText="취소"
        confirmLoading={saving}
        onCancel={() => setOpen(false)}
        onOk={submit}
        destroyOnHidden
        afterOpenChange={applyOnOpen}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {error && <Alert type="error" showIcon message={error} />}
          <Form form={form} layout="vertical" requiredMark={false}>
            <Form.Item name="parentId" label="상위 부서" extra="비워 두면 최상위 부서가 됩니다.">
              <Select allowClear placeholder="최상위" options={parentOptions} />
            </Form.Item>
            <Form.Item
              name="deptCode"
              label="부서 코드"
              rules={[{ required: true, message: '부서 코드를 입력하세요' }, { max: 40 }]}
            >
              <Input placeholder="SALES" />
            </Form.Item>
            <Form.Item
              name="deptName"
              label="부서명"
              rules={[{ required: true, message: '부서명을 입력하세요' }, { max: 200 }]}
            >
              <Input placeholder="영업팀" />
            </Form.Item>
            <Space size="middle" style={{ display: 'flex' }}>
              <Form.Item name="sortOrder" label="정렬 순서" style={{ flex: 1 }}>
                <InputNumber min={0} max={9999} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="isActive" label="사용 여부" valuePropName="checked" style={{ flex: 1 }}>
                <Switch checkedChildren="사용" unCheckedChildren="미사용" />
              </Form.Item>
            </Space>
          </Form>
        </Space>
      </Modal>
    </Space>
  );
}

function findNode(nodes: DepartmentNode[], id: string): DepartmentNode | null {
  for (const node of nodes) {
    if (node.departmentId === id) return node;
    const found = findNode(node.children, id);
    if (found) return found;
  }
  return null;
}
